import { PageType } from '../types.js';
import type { DocManager } from '../doc/manager.js';

/**
 * Daily journal page (IMPL-06). A page is created on first open each day. Ids are
 * deterministic so the same day always resolves to the same note across devices.
 */
const PREFIX = 'journal-';

/** Format a Date as a UTC YYYY-MM-DD key. Strings are passed through (already a key). */
export function toDateKey(date: Date | string): string {
  if (typeof date === 'string') return date;
  return date.toISOString().slice(0, 10);
}

export function dailyJournalNoteId(date: Date | string): string {
  return `${PREFIX}${toDateKey(date)}`;
}

export function isDailyJournalId(noteId: string): boolean {
  return /^journal-\d{4}-\d{2}-\d{2}$/.test(noteId);
}

/**
 * Resolve (creating if needed) today's journal note. Idempotent: the first open of a day
 * creates it; later opens return the same note.
 */
export async function ensureDailyJournal(
  manager: DocManager,
  userId: string,
  date: Date | string,
): Promise<string> {
  const noteId = dailyJournalNoteId(date);
  const existing = await manager.listLocalDocs();
  if (existing.some((m) => m.noteId === noteId)) {
    await manager.openDoc(noteId);
    return noteId;
  }
  await manager.createDoc(noteId, userId, PageType.Journal);
  await manager.indexDoc(noteId, userId);
  return noteId;
}
